/**
 * BrandingContext — the app's single brand-identity read path.
 *
 * Resolves the five brand-identity values (tenant name, logo, primary color,
 * accent color, header text) from the `BrandingConfig` record for the active
 * tenant and hands them to the tree as one fully-resolved `BrandIdentity`, so no
 * component has to keep a brand literal of its own.
 *
 * Requirements: 16.4, 16.5 (original single-tenant branding)
 * demo-tenant-environment Task 22 — Requirements 1.1, 1.2, 1.3, 1.4, 1.5
 *
 * THREE THINGS TASK 22 ADDED, AND WHY
 *
 * 1. A NEUTRAL FALLBACK WITH NO TENANT IN IT (Req 1.3, 1.4). This file used to
 *    carry a `DEFAULT_BRANDING` constant naming "Idaho Pageants" and substitute it
 *    whenever the record was missing or the read failed. In a sales demo that
 *    default is the leak: a prospect's own tenant, one failed read away from
 *    displaying the real client's name. The fallback is now
 *    `NEUTRAL_BRAND_IDENTITY` — a generic name, no logo, no color override.
 *
 * 2. A 3-SECOND BUDGET (Req 1.4). A GraphQL call that never settles used to leave
 *    the UI in `isLoading` forever. The read now races a 3s deadline; whichever
 *    lands first decides, and a response arriving after the deadline is ignored
 *    rather than swapping the identity out from under a rendered page. The late
 *    value is not lost — it is what the next reload resolves (Req 1.5).
 *
 * 3. COLORS AS DESIGN TOKENS, NOT INLINE STYLES (Req 1.1). A resolved identity's
 *    colors are written onto `document.documentElement` as `--primary` / `--accent`
 *    — the tokens declared in `styles/tokens.css` that every Tailwind utility in
 *    the app already reads. One write re-skins every surface; nothing needs a
 *    per-component `style={{ color }}`. The neutral identity writes nothing, so
 *    the shipped palette stands.
 *
 * NEVER THROWS. Absent record, GraphQL error, network failure, and timeout all
 * land on the same neutral identity, and the provider always renders `children` —
 * so the rest of the page survives a branding failure (Req 1.4).
 *
 * NO SCHEMA CHANGE. This reads the shipped year-keyed `getBrandingConfig` query.
 * The active-tenant dimension rides on the tenant-foundation Task 18 overlay: the
 * active tenant's canonical `Tenant.name` (via the viewer's own Memberships)
 * outranks the record's `pageantName`. A branding edit is therefore pure data —
 * `updateBrandingConfig` writes the record, the next reload reads it, and no
 * component source changes and nothing is redeployed (Req 1.5).
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { BrandingConfig } from '@/types';
import { getBrandingConfig } from '@/graphql/queries';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import {
  brandIdentityCssVars,
  resolveBrandIdentity,
  NEUTRAL_BRAND_IDENTITY,
  type BrandIdentity,
} from '@/lib/tenantBranding';

const client = generateClient();

/**
 * The retrieval budget from Req 1.4. Exported so a test can reason about the
 * deadline without re-deriving the number.
 */
export const BRANDING_FETCH_TIMEOUT_MS = 3000;

/** The design tokens a resolved identity may override, so they can be cleaned up. */
const BRANDING_TOKEN_NAMES = [
  '--primary',
  '--accent',
  '--branding-primary-color',
  '--branding-accent-color',
] as const;

/** Resolution state of the branding read. */
export type BrandingResolutionStatus = 'loading' | 'resolved' | 'unresolved';

export interface BrandingContextValue {
  /**
   * The retrieved `BrandingConfig`, or `null` when the read is in flight, returned
   * nothing, failed, or timed out. Prefer `identity` — this is the raw record,
   * kept for the consumers that read it directly, and it is deliberately NOT
   * back-filled with a tenant-specific default.
   */
  branding: BrandingConfig | null;
  /**
   * The five brand-identity values, always fully resolved. Read this (not
   * `branding?.pageantName` and not a literal) wherever brand identity is
   * displayed. `identity.isNeutral` marks the tenant-agnostic fallback.
   */
  identity: BrandIdentity;
  /**
   * The effective brand (pageant) name — `identity.name`. Retained as its own
   * field because existing consumers read it.
   */
  brandName: string;
  /** Whether branding is still being retrieved (and no deadline has passed yet). */
  isLoading: boolean;
  /** `'unresolved'` covers absent, failed, and timed-out alike. */
  status: BrandingResolutionStatus;
  /** Error message if branding failed to load */
  error: string | null;
  /** Current competition year */
  competitionYear: number;
  /** Refresh branding configuration */
  refresh: () => Promise<void>;
  /** Get the full logo URL from S3 key */
  getLogoUrl: (s3Key: string) => string;
  /** The resolved logo URL, or `null` for the neutral identity (no logo). */
  logoUrl: string | null;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export interface BrandingProviderProps {
  /** Child components */
  children: React.ReactNode;
  /** Competition year to fetch branding for */
  competitionYear?: number;
  /** S3 bucket URL for branding assets */
  brandingBucketUrl?: string;
  /**
   * Retrieval budget in ms (Req 1.4). Overridable so a test can drive the
   * timeout branch without a 3s wall-clock wait; production uses the default.
   */
  fetchTimeoutMs?: number;
  /**
   * Write resolved colors onto `document.documentElement`. On by default — the
   * app has one provider and one document. A nested/preview provider passes
   * `false` so it cannot repaint the whole app.
   */
  applyColorTokens?: boolean;
}

/**
 * Get the current competition year
 * Uses the current year, or next year if we're past September
 */
function getCurrentCompetitionYear(): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  // If past September, use next year
  return month >= 9 ? year + 1 : year;
}

/** Sentinel resolved by the deadline timer, distinguishable from a real payload. */
const TIMED_OUT = Symbol('branding-timeout');

/**
 * The one public-facing marketing site's tenant, read when there is no
 * session at all (tenant-foundation Task 18 / D4). `BrandingConfig` is now
 * tenant-scoped, so a request needs SOME tenantId — for a genuinely
 * unauthenticated visitor there is no claim to read, and the public site has
 * always been, and remains, Idaho's. This is NOT used for an authenticated
 * session that simply has no `activeTenantId` yet (pre-Membership onboarding,
 * e.g. `ContactIntakeForm`) — defaulting THAT case to Idaho would reproduce
 * the exact cross-tenant leak this re-key exists to close.
 */
const PUBLIC_MARKETING_SITE_TENANT_ID = 'tenant-idaho';

/**
 * BrandingProvider Component
 *
 * Fetches and provides branding configuration to all child components.
 */
export function BrandingProvider({
  children,
  competitionYear: propCompetitionYear,
  brandingBucketUrl = import.meta.env.VITE_BRANDING_BUCKET_URL || '',
  fetchTimeoutMs = BRANDING_FETCH_TIMEOUT_MS,
  applyColorTokens = true,
}: BrandingProviderProps): React.ReactElement {
  const competitionYear = propCompetitionYear ?? getCurrentCompetitionYear();

  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [status, setStatus] = useState<BrandingResolutionStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef<boolean>(true);
  const fetchIdRef = useRef<number>(0);

  // Resolved BEFORE the fetch, not after: `getBrandingConfig` is tenant-scoped
  // now, so the query needs a tenantId to even issue. `authLoading` gates the
  // fetch below — firing it before auth settles would read `!user` as "public"
  // and briefly resolve Idaho's identity for an authenticated meridian user
  // whose session just hasn't loaded yet.
  const { user, isLoading: authLoading } = useAuth();
  const activeTenantId = user?.activeTenantId;

  const fetchBranding = useCallback(async () => {
    const seq = ++fetchIdRef.current;
    if (mountedRef.current) {
      setStatus('loading');
      setError(null);
    }

    // A stale write is as bad as a wrong one: `refresh()` and the deadline can
    // both be in flight, so every setState below is guarded on "still mounted AND
    // still the newest read".
    const isCurrent = (): boolean => mountedRef.current && seq === fetchIdRef.current;

    // No session at all → the one public marketing site, Idaho's (unchanged).
    // A session with no `activeTenantId` yet (pre-Membership onboarding) has
    // no tenant to query FOR — not "query for Idaho's" — so it resolves
    // straight to the neutral identity, no network call, no cross-tenant read.
    const tenantId = activeTenantId ?? (user ? null : PUBLIC_MARKETING_SITE_TENANT_ID);
    if (!tenantId) {
      setBranding(null);
      setStatus('unresolved');
      setError(null);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<typeof TIMED_OUT>((resolve) => {
      timer = setTimeout(() => resolve(TIMED_OUT), fetchTimeoutMs);
    });

    try {
      const outcome = await Promise.race([
        client.graphql({
          query: getBrandingConfig,
          variables: { tenantId, competitionYear },
        }) as Promise<{ data?: { getBrandingConfig?: BrandingConfig | null } | null }>,
        deadline,
      ]);

      if (!isCurrent()) return;

      if (outcome === TIMED_OUT) {
        // Req 1.4: past the budget we commit to the neutral identity. A response
        // that lands later is ignored — it becomes the next reload's answer
        // rather than re-branding a page the viewer is already reading.
        setBranding(null);
        setStatus('unresolved');
        setError('Branding configuration did not load in time');
        return;
      }

      const config = outcome?.data?.getBrandingConfig ?? null;
      setBranding(config);
      // A returned record is resolved; an absent one is `unresolved`, which is
      // what selects the neutral identity (Req 1.4).
      setStatus(config ? 'resolved' : 'unresolved');
    } catch (err) {
      console.error('Failed to fetch branding config:', err);
      if (!isCurrent()) return;
      // Fail to NEUTRAL, never to a tenant literal.
      setBranding(null);
      setStatus('unresolved');
      setError('Failed to load branding configuration');
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }, [competitionYear, fetchTimeoutMs, user, activeTenantId]);

  useEffect(() => {
    mountedRef.current = true;
    // Wait for auth to settle before resolving "no claim" as "public" — see
    // `fetchBranding`'s own comment on why firing early would flicker Idaho's
    // identity for an authenticated user mid-load.
    if (!authLoading) void fetchBranding();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchBranding, authLoading]);

  const getLogoUrl = useCallback((s3Key: string): string => {
    if (!s3Key) return '';
    if (s3Key.startsWith('http')) return s3Key;
    return `${brandingBucketUrl}/${s3Key}`;
  }, [brandingBucketUrl]);

  // White-label (tenant-foundation Task 18): the active tenant's own canonical
  // `Tenant.name` — surfaced client-side as the matching Membership's
  // `tenantName` — outranks the year-keyed `pageantName`, so the authenticated
  // app reads as the tenant the viewer is actually in. Public / pre-auth has no
  // active tenant, so it reads the record (or the neutral identity). `user` /
  // `activeTenantId` are resolved once, above, alongside the fetch itself.
  const { memberships } = useMyMemberships();

  const identity = useMemo<BrandIdentity>(
    () =>
      resolveBrandIdentity({
        activeTenantId,
        memberships,
        // Anything other than a resolved record — in flight, absent, failed,
        // timed out — is passed as `null` and answered with the neutral identity.
        config: status === 'resolved' ? branding : null,
      }),
    [status, branding, activeTenantId, memberships],
  );

  const brandName = identity.name;
  const logoUrl = identity.logoS3Key ? getLogoUrl(identity.logoS3Key) : null;

  // Reflect the effective brand in the browser tab title.
  useEffect(() => {
    if (brandName) document.title = brandName;
  }, [brandName]);

  // Colors ride the design tokens (see file header). Neutral writes nothing and
  // clears anything a previous resolution wrote, so the app falls back to its own
  // palette rather than keeping a stale tenant's colors.
  useEffect(() => {
    if (!applyColorTokens || typeof document === 'undefined') return;
    const root = document.documentElement;
    const vars = brandIdentityCssVars(identity);
    for (const name of BRANDING_TOKEN_NAMES) {
      const value = vars[name];
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }
    return () => {
      for (const name of BRANDING_TOKEN_NAMES) root.style.removeProperty(name);
    };
  }, [applyColorTokens, identity]);

  const contextValue = useMemo<BrandingContextValue>(() => ({
    branding,
    identity,
    brandName,
    isLoading: status === 'loading',
    status,
    error,
    competitionYear,
    refresh: fetchBranding,
    getLogoUrl,
    logoUrl,
  }), [branding, identity, brandName, status, error, competitionYear, fetchBranding, getLogoUrl, logoUrl]);

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * useBranding Hook
 * 
 * Access branding configuration from any component.
 * Must be used within a BrandingProvider.
 */
export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

/**
 * CSS custom properties for a branding record, for a scoped subtree.
 *
 * Delegates to `brandIdentityCssVars` so a subtree and the document root apply
 * colors through exactly one rule set. A `null` record contributes nothing, which
 * is what leaves the shipped design tokens in place for the neutral identity.
 */
export function getBrandingCssVars(
  branding: Pick<BrandingConfig, 'primaryColor' | 'accentColor'> | null,
): React.CSSProperties {
  const source = branding ?? NEUTRAL_BRAND_IDENTITY;
  return brandIdentityCssVars({
    primaryColor: source.primaryColor ?? null,
    accentColor: source.accentColor ?? null,
  }) as React.CSSProperties;
}

export default BrandingContext;
